import { fetchLlmModels } from "@app/deploy";
import { useLoader, useQuery } from "@app/react";
import type { DeployLlmPolicy } from "@app/types";
import { InputSearch } from "@app/ui/shared/input";
import { LoadingSpinner } from "@app/ui/shared/loading";
import { useState } from "react";

interface AllowedModelsListProps {
  policy: DeployLlmPolicy | null | undefined;
  isLoading: boolean;
}

export const AllowedModelsList = ({
  policy,
  isLoading,
}: AllowedModelsListProps) => {
  const [allowedModelsSearch, setAllowedModelsSearch] = useState("");
  useQuery(fetchLlmModels());
  const {
    meta: { models: allModels = [] },
    isLoading: isModelsLoading,
    isInitialLoading: isModelsInitialLoading,
  } = useLoader(fetchLlmModels);
  const allModelIds = allModels.map((m: { id: string }) => m.id);

  const allowedModelIds = policy?.models ?? [];
  const displayModelIds =
    allowedModelIds.length > 0 ? allowedModelIds : allModelIds;
  const searchTerm = allowedModelsSearch.trim().toLowerCase();

  let filteredAllowedModels: string[] = [];
  if (searchTerm === "") {
    filteredAllowedModels = displayModelIds;
  } else {
    filteredAllowedModels = displayModelIds.filter((id: string) =>
      id.toLowerCase().includes(searchTerm),
    );
  }

  if (isLoading || isModelsLoading || isModelsInitialLoading) {
    return <LoadingSpinner />;
  }

  let message = "";
  if (displayModelIds.length === 0) {
    message = "No models available";
  } else if (filteredAllowedModels.length === 0) {
    message = "No models match search";
  }

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-base font-semibold text-gray-900">
        Allowed Models ({filteredAllowedModels.length})
      </h4>
      <div className="border border-gray-200 rounded-md p-4">
        <InputSearch
          id="allowed-models-search"
          placeholder="Search..."
          search={allowedModelsSearch ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setAllowedModelsSearch(e.target.value)
          }
          className="w-full mb-4"
        />
        <div className="mt-1 max-h-48 flex-col gap-1 overflow-y-auto">
          {message ? (
            <span className="text-gray-600">{message}</span>
          ) : (
            filteredAllowedModels.map((id: string) => (
              <div key={id} className="pb-2">
                <span className="border-b border-dotted border-gray-400 pb-0.5 text-black">
                  {id}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
